<script setup lang="ts">
/**
 * Error page for `ErrorCode.AuthCodeTokenExchangeFailed` (HTTP 401 from the BFF).
 *
 * The BFF returns this code when it cannot exchange the authorization code
 * for tokens (e.g. expired code, replayed code, Keycloak returned an error).
 * Unlike the thin `AuthErrorView` wrappers, this page reads
 * `route.query.errorMessage` and passes it to `errorExplanationFromBackend`
 * so the backend's human-readable message is surfaced to the user instead of
 * the generic fallback. Shows a "Go To Login" button.
 */
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import ErrorView from '@/components/errors/ErrorView.vue'
import { redirectToLogin } from '@/api/keycloak_api'
import { ErrorCode } from '@/errors/codes'
import {
  DEFAULT_AUTH_ERROR_ACTIONS,
  ERROR_USER_MESSAGES,
  errorExplanationFromBackend,
} from '@/errors/error_user_messages'

const route = useRoute()

const page = ERROR_USER_MESSAGES[ErrorCode.AuthCodeTokenExchangeFailed]
const title = page.title
const explanation = computed(() =>
  errorExplanationFromBackend(ErrorCode.AuthCodeTokenExchangeFailed, route.query.errorMessage),
)
const actions = page.actions ?? DEFAULT_AUTH_ERROR_ACTIONS

function goToLogin() {
  redirectToLogin()
}
</script>

<template>
  <ErrorView
    :code="ErrorCode.AuthCodeTokenExchangeFailed"
    :title="title"
    :explanation="explanation"
    :actions="actions"
  >
    <button class="tufte-button" @click="goToLogin">Go To Login</button>
  </ErrorView>
</template>
